import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import { useEntities } from '../hooks/useEntities';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import FilterBar from '../components/ui/FilterBar';
import ActionButton from '../components/ui/ActionButton';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';

const API = `${API_V1}/debt`;
const COV_API = `${API_V1}/covenants`;
const PAGE_SIZE = 20;

const RATIO_LABELS: Record<string, string> = {
  CURRENT_RATIO: 'Current Ratio',
  LEVERAGE: 'Leverage (Debt / EBITDA)',
  INTEREST_COVERAGE: 'Interest Coverage (EBITDA / Interest)',
  NET_WORTH: 'Minimum Tangible Net Worth',
  DSCR: 'Debt Service Coverage Ratio',
};
const COMPARISON_LABELS: Record<string, string> = { LT: '<', LTE: '≤', GT: '>', GTE: '≥', EQ: '=' };
const COVENANT_STATUS_COLORS: Record<string, string> = {
  OK: 'var(--positive)', WARNING: 'var(--warning)', BREACHED: 'var(--negative)',
};

interface Facility {
  id: number;
  facility_number: string;
  facility_type: string;
  lender_name: string | null;
  entity_code: string | null;
  original_principal: number;
  current_principal: number;
  committed_amount: number | null;
  drawn_amount: number;
  interest_rate: number;
  rate_type: string;
  rate_benchmark: string | null;
  rate_spread: number | null;
  start_date: string | null;
  maturity_date: string | null;
  term_months: number | null;
  payment_frequency: string;
  payment_amount: number | null;
  currency: string;
  debt_account: string | null;
  current_portion_account: string | null;
  interest_expense_account: string | null;
  interest_payable_account: string | null;
  cash_account: string | null;
  gl_resolution_method: string | null;
  gl_resolution_confidence: number | null;
  collateral_description: string | null;
  security_type: string | null;
  lifecycle_status: string;
  version: number;
  schedule?: any[];
  drawdowns?: any[];
  covenants?: any[];
}

interface Summary {
  total_facilities: number;
  active: number;
  total_outstanding: number;
  weighted_avg_rate: number;
  next_maturity: { date: string; facility_number: string; amount: number } | null;
  undrawn_capacity: number;
  variable_rate_pct: number;
  covenant_headroom_pct: number | null;
  covenants_breached: number;
}

const STATUSES = ['DRAFT', 'PENDING_APPROVAL', 'ACTIVE', 'MODIFIED', 'MATURED', 'REPAID'];
const TYPES = ['TERM_LOAN', 'REVOLVING_CREDIT', 'BOND', 'NOTE_PAYABLE', 'LINE_OF_CREDIT'];
const FREQUENCIES = ['MONTHLY', 'QUARTERLY', 'SEMI_ANNUAL', 'ANNUAL', 'BULLET'];
const TABS = ['Facilities', 'Payment Schedule', 'Maturity Profile'];

const emptyForm = {
  facility_type: 'TERM_LOAN', lender_name: '', original_principal: '',
  interest_rate: '', rate_type: 'FIXED', rate_benchmark: '', rate_spread: '',
  start_date: '', maturity_date: '', payment_frequency: 'MONTHLY',
  currency: 'USD', entity_code: '', collateral_description: '', security_type: '',
};

export default function DebtManagement() {
  const entities = useEntities();
  const { entity } = useGlobalEntity();
  const [tab, setTab] = useState('Facilities');
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [expandedData, setExpandedData] = useState<Facility | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [glPreview, setGlPreview] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Schedule + maturity data
  const [schedule, setSchedule] = useState<any[]>([]);
  const [maturity, setMaturity] = useState<any[]>([]);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (statusFilter) p.set('status', statusFilter);

    try {
      const [listRes, sumRes] = await Promise.all([
        authFetch(withEntityParam(`${API}?${p}`, entity)),
        authFetch(withEntityParam(`${API}/summary`, entity)),
      ]);
      if (listRes.ok) {
        const d = await listRes.json();
        setFacilities(d.items || []);
        setTotal(d.total || 0);
      }
      if (sumRes.ok) setSummary(await sumRes.json());
    } catch (e) { console.error(e); }
  }, [page, statusFilter, entity]);

  const loadSchedule = useCallback(async () => {
    try {
      const res = await authFetch(withEntityParam(`${API}/schedule?limit=60`, entity));
      if (res.ok) setSchedule(await res.json());
    } catch (e) { console.error(e); }
  }, [entity]);

  const loadMaturity = useCallback(async () => {
    try {
      const res = await authFetch(withEntityParam(`${API}/maturity-profile`, entity));
      if (res.ok) setMaturity(await res.json());
    } catch (e) { console.error(e); }
  }, [entity]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'Payment Schedule') loadSchedule(); }, [tab, loadSchedule]);
  useEffect(() => { if (tab === 'Maturity Profile') loadMaturity(); }, [tab, loadMaturity]);

  // Load GL preview when create modal opens or entity changes
  useEffect(() => {
    if (!showCreate) return;
    const p = new URLSearchParams();
    if (form.entity_code) p.set('entity_code', form.entity_code);
    authFetch(`${API}/preview-gl?${p}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setGlPreview(d))
      .catch(() => setGlPreview(null));
  }, [showCreate, form.entity_code]);

  const toggleExpand = async (id: number) => {
    if (expandedId === id) { setExpandedId(null); setExpandedData(null); return; }
    try {
      const res = await authFetch(`${API}/${id}`);
      if (res.ok) { setExpandedData(await res.json()); setExpandedId(id); }
    } catch (e) { console.error(e); }
  };

  const handleCreate = async () => {
    // Pre-flight validation — fail fast
    if (!form.original_principal || parseFloat(form.original_principal) <= 0) {
      showToast('Principal Amount is required and must be > 0', false); return;
    }
    if (form.interest_rate === '' || parseFloat(form.interest_rate) < 0) {
      showToast('Interest Rate is required', false); return;
    }
    if (!form.start_date) { showToast('Start Date is required', false); return; }
    if (!form.maturity_date) { showToast('Maturity Date is required', false); return; }
    if (form.maturity_date <= form.start_date) {
      showToast('Maturity Date must be after Start Date', false); return;
    }

    try {
      const body: any = { ...form };
      body.original_principal = parseFloat(body.original_principal);
      body.interest_rate = parseFloat(body.interest_rate);
      if (body.rate_spread) body.rate_spread = parseFloat(body.rate_spread);
      else delete body.rate_spread;
      if (!body.rate_benchmark) delete body.rate_benchmark;
      if (!body.entity_code) delete body.entity_code;
      if (!body.collateral_description) delete body.collateral_description;
      if (!body.security_type) delete body.security_type;

      const res = await authFetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const d = await res.json();
        showToast(`Facility ${d.facility_number} created in DRAFT. GL: ${d.gl_resolution_method || 'N/A'} (${d.gl_resolution_confidence || 0}%)`);
        setShowCreate(false);
        setForm({ ...emptyForm });
        load();
      } else {
        const e = await res.json().catch(() => ({}));
        showToast(`Create failed: ${e.detail || `HTTP ${res.status}`}`, false);
      }
    } catch (e: any) {
      showToast(`Error: ${e?.message || 'Network error'}`, false);
    }
  };

  const handleSubmitForApproval = async (id: number) => {
    try {
      const res = await authFetch(`${API}/${id}/submit-for-approval`, { method: 'POST' });
      if (res.ok) { showToast('Submitted for approval'); load(); setExpandedId(null); }
      else { const e = await res.json().catch(() => ({})); showToast(e.detail || 'Failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authFetch(`${API}/upload`, { method: 'POST', body: fd });
      if (res.ok) {
        const d = await res.json();
        if (d.extracted) {
          showToast(`Extracted: ${d.lender_name || 'Unknown'} — $${formatAmount(d.original_principal)} @ ${(d.interest_rate * 100).toFixed(2)}%. ${d.covenants_created?.length || 0} covenants linked.`);
          load();
        } else {
          showToast(d.note || 'Extraction failed', false);
        }
      } else {
        showToast('Upload failed', false);
      }
    } catch (e) { showToast('Upload error', false); }
    setUploading(false);
  };

  const handleAction = async (id: number, action: string, version: number) => {
    try {
      const res = await authFetch(`${API}/${id}/${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expected_version: version }),
      });
      if (res.ok) { showToast(`${action} completed`); load(); setExpandedId(null); }
      else {
        const e = await res.json().catch(() => ({}));
        showToast(e.detail || `${action} failed`, false);
      }
    } catch (e) { showToast(`Error: ${action}`, false); }
  };

  const handleMonitorCovenants = async (refreshFacilityId?: number) => {
    try {
      const res = await authFetch(`${COV_API}/monitor`, { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        const monitored = d.monitored ?? 0;
        const breached = d.breached ?? 0;
        const alerts: string[] = Array.isArray(d.alerts) ? d.alerts : [];
        // Concise summary on top line, then enumerate each alert below
        // so operator sees WHICH covenants breached / warned without
        // having to drill into a separate view.
        const detail = alerts.length > 0 ? '\n' + alerts.join('\n') : '';
        showToast(
          `Monitored ${monitored} covenants — ${breached} breached${detail}`,
          breached === 0,
        );
        load();
        if (refreshFacilityId != null && expandedId === refreshFacilityId) {
          const r = await authFetch(`${API}/${refreshFacilityId}`);
          if (r.ok) setExpandedData(await r.json());
        }
      } else {
        showToast('Monitoring failed', false);
      }
    } catch (e) { showToast('Monitoring error', false); }
  };

  const handleAccrue = async (facilityId: number, period: string) => {
    try {
      const res = await authFetch(`${API}/${facilityId}/accrue/${period}`, { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        showToast(`Posted: interest $${d.interest?.toFixed(2)}, principal $${d.principal?.toFixed(2)}`);
        toggleExpand(facilityId); // refresh detail
      } else {
        const e = await res.json().catch(() => ({}));
        showToast(e.detail || 'Accrual failed', false);
      }
    } catch (e) { showToast('Accrual error', false); }
  };

  const lbl = { fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase' as const, marginBottom: 2 };
  const cellPad = { padding: '6px 10px' };
  const thStyle = { ...cellPad, fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase' as const, textAlign: 'left' as const, borderBottom: '2px solid var(--border-subtle)' };
  const tdStyle = { ...cellPad, fontSize: 13, borderBottom: '1px solid var(--border-subtle)' };

  return (
    <PageLayout
      title="Debt Management"
      subtitle="Track debt facilities, amortization, interest accruals, and covenant compliance"
      actions={[
        { label: '+ New Facility', onClick: () => setShowCreate(true) },
        { label: 'Upload Loan Agreement', onClick: () => document.getElementById('debt-pdf-input')?.click() },
        // Page-level "Run Covenant Monitoring" removed 2026-05-28 per Albert
        // — covenants are now auto-monitored hourly by the
        // monitor_covenants_all scheduler. Per-facility on-demand
        // refresh button remains inside each expanded facility detail.
      ]}
    >
      <input id="debt-pdf-input" type="file" accept=".pdf" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }} />

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, padding: '10px 20px',
          borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, maxWidth: 500,
          background: toast.ok ? '#16a34a' : '#ef4444',
          whiteSpace: 'pre-line' }}>
          {toast.msg}
        </div>
      )}

      {uploading && (
        <div style={{ padding: 16, background: 'var(--accent-subtle)', borderRadius: 8, marginBottom: 16, textAlign: 'center' }}>
          Extracting loan agreement with AI...
        </div>
      )}

      {/* KPI Grid */}
      {summary && (
        <KpiGrid items={[
          { label: 'Total Outstanding', value: formatAmountCompact(summary.total_outstanding) },
          { label: 'Weighted Avg Rate', value: `${summary.weighted_avg_rate}%` },
          { label: 'Active Facilities', value: String(summary.active) },
          { label: 'Next Maturity', value: summary.next_maturity ? `${summary.next_maturity.date} (${formatAmountCompact(summary.next_maturity.amount)})` : 'None' },
          { label: 'Undrawn Capacity', value: formatAmountCompact(summary.undrawn_capacity) },
          { label: 'Covenant Headroom', value: summary.covenant_headroom_pct != null ? `${summary.covenant_headroom_pct}%` : 'N/A',
            color: summary.covenants_breached > 0 ? 'var(--negative)' : undefined },
        ]} />
      )}

      {/* Tabs */}
      <div style={{ marginTop: 16 }}>
        <TabBar tabs={TABS.map(t => ({ key: t, label: t }))} active={tab} onChange={v => setTab(v)} />
      </div>

      {/* ═══ TAB: Facilities ═══ */}
      {tab === 'Facilities' && (
        <>
          <FilterBar filters={[
            { key: 'status', label: 'Status', type: 'select', value: statusFilter,
              onChange: (v: string) => { setStatusFilter(v); setPage(0); },
              options: STATUSES.map(s => ({ value: s, label: s })) },
          ]} />

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Facility #', 'Type', 'Lender', 'Entity', 'Principal', 'Rate', 'Maturity', 'Status', 'Actions'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {facilities.length === 0 ? (
                <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-hint)' }}>No facilities yet.</td></tr>
              ) : facilities.map(f => (
                <>
                  <tr key={f.id} onClick={() => toggleExpand(f.id)}
                    style={{ cursor: 'pointer', background: expandedId === f.id ? 'var(--accent-subtle)' : 'transparent' }}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{f.facility_number}</td>
                    <td style={tdStyle}>{f.facility_type.replace(/_/g, ' ')}</td>
                    <td style={tdStyle}>{f.lender_name || '-'}</td>
                    <td style={tdStyle}>{f.entity_code || '-'}</td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{formatAmount(f.current_principal)}</td>
                    <td style={tdStyle}>{(f.interest_rate * 100).toFixed(2)}% {f.rate_type === 'VARIABLE' ? `(${f.rate_benchmark || 'VAR'})` : ''}</td>
                    <td style={tdStyle}>{f.maturity_date || '-'}</td>
                    <td style={tdStyle}><StatusBadge status={f.lifecycle_status} /></td>
                    <td style={tdStyle} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {f.lifecycle_status === 'DRAFT' && (
                          <ActionButton label="Submit" size="sm" onClick={() => handleSubmitForApproval(f.id)} />
                        )}
                        {f.lifecycle_status === 'PENDING_APPROVAL' && (
                          <ActionButton label="Approve & Activate" size="sm" variant="success" onClick={() => handleAction(f.id, 'activate', f.version)} />
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expandable detail row */}
                  {expandedId === f.id && expandedData && (
                    <tr key={`${f.id}-detail`}>
                      <td colSpan={9} style={{ padding: '0 14px 16px 14px', background: 'var(--accent-subtle)' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>

                          {/* Facility Details Grid */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
                            {[
                              { l: 'Original Principal', v: formatAmount(expandedData.original_principal) },
                              { l: 'Current Principal', v: formatAmount(expandedData.current_principal) },
                              { l: 'Interest Rate', v: `${(expandedData.interest_rate * 100).toFixed(2)}%` },
                              { l: 'Rate Type', v: `${expandedData.rate_type}${expandedData.rate_benchmark ? ` + ${expandedData.rate_benchmark}` : ''}` },
                              { l: 'Term', v: expandedData.term_months ? `${expandedData.term_months} months` : '-' },
                              { l: 'Payment Freq', v: expandedData.payment_frequency },
                              { l: 'Start Date', v: expandedData.start_date || '-' },
                              { l: 'Maturity Date', v: expandedData.maturity_date || '-' },
                              { l: 'Currency', v: expandedData.currency },
                              { l: 'Security', v: expandedData.security_type || 'N/A' },
                              { l: 'Payment Amount', v: expandedData.payment_amount ? formatAmount(expandedData.payment_amount) : 'Calculated' },
                              { l: 'Collateral', v: expandedData.collateral_description || 'None' },
                            ].map(d => (
                              <div key={d.l} style={{ padding: 8, background: 'var(--bg-card)', borderRadius: 6 }}>
                                <div style={lbl}>{d.l}</div>
                                <div style={{ fontSize: 12 }}>{d.v}</div>
                              </div>
                            ))}
                          </div>

                          {/* GL Accounts */}
                          <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg-card)' }}>
                            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>GL Accounts (resolved from CoA)</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                              {[
                                { l: 'Debt Liability', v: expandedData.debt_account },
                                { l: 'Current Portion', v: expandedData.current_portion_account },
                                { l: 'Interest Expense', v: expandedData.interest_expense_account },
                                { l: 'Interest Payable', v: expandedData.interest_payable_account },
                                { l: 'Cash / Bank', v: expandedData.cash_account },
                              ].map(d => (
                                <div key={d.l}>
                                  <div style={lbl}>{d.l}</div>
                                  <div style={{ fontSize: 12, fontFamily: 'monospace', color: d.v ? 'var(--accent)' : 'var(--text-hint)' }}>
                                    {d.v || 'Not resolved'}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 4 }}>
                              Method: {expandedData.gl_resolution_method || 'N/A'} | Confidence: {expandedData.gl_resolution_confidence ?? 'N/A'}%
                            </div>
                          </div>

                          {/* Covenants — always render so the operator sees
                              "no covenants linked" instead of nothing.
                              2026-05-28 fix: the prior `length > 0` guard
                              made the section silently disappear when
                              covenants existed in CovenantConfig but the
                              DebtCovenant FK link table was empty (the
                              read-side fallback in _get_covenants now
                              handles this) — and made it indistinguishable
                              from "facility has zero covenants" either way. */}
                          <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg-card)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                              <div style={{ fontSize: 12, fontWeight: 700 }}>
                                Covenants ({(expandedData.covenants || []).length})
                              </div>
                              <ActionButton label="Run Monitoring" size="sm"
                                onClick={() => handleMonitorCovenants(expandedData.id)} />
                            </div>
                            {(!expandedData.covenants || expandedData.covenants.length === 0) && (
                              <div style={{ fontSize: 12, color: 'var(--text-hint)', fontStyle: 'italic', padding: '8px 0' }}>
                                No covenants linked. Upload a loan agreement PDF or add covenants manually via the Covenant API.
                              </div>
                            )}
                            {expandedData.covenants && expandedData.covenants.length > 0 && (
                            <div style={{ marginTop: 0 }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                    {['Covenant', 'Current Value', 'Threshold', 'Headroom', 'Status'].map((h, i) => (
                                      <th key={h} style={{
                                        textAlign: i === 0 ? 'left' : 'right',
                                        padding: '4px 8px', fontSize: 10, fontWeight: 700,
                                        color: 'var(--text-hint)', textTransform: 'uppercase',
                                      }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {expandedData.covenants.map((c: any) => {
                                    const hasValue = c.calculated_value != null;
                                    const status = c.is_breached === true
                                      ? 'BREACHED'
                                      : hasValue && c.headroom_pct != null && c.headroom_pct < 10
                                        ? 'WARNING'
                                        : hasValue
                                          ? 'OK'
                                          : null;
                                    return (
                                      <tr key={c.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                        <td style={{ padding: '6px 8px' }}>
                                          <div style={{ fontWeight: 600 }}>{c.covenant_name}</div>
                                          <div style={{ fontSize: 10, color: 'var(--text-hint)' }}>
                                            {RATIO_LABELS[c.ratio_type] || c.ratio_type} {COMPARISON_LABELS[c.comparison || 'LTE'] || '≤'} {c.threshold}
                                          </div>
                                        </td>
                                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>
                                          {hasValue ? c.calculated_value
                                            : <span style={{ color: 'var(--text-hint)' }}>—</span>}
                                        </td>
                                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{c.threshold}</td>
                                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                                          {c.headroom_pct != null ? `${c.headroom_pct}%`
                                            : <span style={{ color: 'var(--text-hint)' }}>—</span>}
                                        </td>
                                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                                          {status
                                            ? <span style={{ fontWeight: 700, color: COVENANT_STATUS_COLORS[status] }}>{status}</span>
                                            : <span style={{ color: 'var(--text-hint)', fontSize: 10 }}>NOT RUN</span>}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            )}
                          </div>

                          {/* Amortization Schedule */}
                          {expandedData.schedule && expandedData.schedule.length > 0 && (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
                                AMORTIZATION SCHEDULE ({expandedData.schedule.length} periods)
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                <thead>
                                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                    {['Period', 'Payment', 'Interest', 'Principal', 'Opening Bal', 'Closing Bal', 'Status', 'GL / AP', ''].map(h => (
                                      <th key={h} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 10, fontWeight: 700 }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {expandedData.schedule.map((s: any) => (
                                    <tr key={s.period} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                      <td style={{ padding: '4px 8px' }}>{s.period}</td>
                                      <td style={{ padding: '4px 8px', fontWeight: 600 }}>{formatAmount(s.payment)}</td>
                                      <td style={{ padding: '4px 8px' }}>{formatAmount(s.interest)}</td>
                                      <td style={{ padding: '4px 8px' }}>{formatAmount(s.principal)}</td>
                                      <td style={{ padding: '4px 8px' }}>{formatAmount(s.opening_balance)}</td>
                                      <td style={{ padding: '4px 8px' }}>{formatAmount(s.closing_balance)}</td>
                                      <td style={{ padding: '4px 8px' }}><StatusBadge status={s.status} size="sm" /></td>
                                      {/* GL/AP cross-references — added 2026-05-28 after Albert
                                          reported "JE not on GL" despite POSTED status. JE
                                          links to GL Workbench detail; AP-invoice number
                                          follows DEBT-{facility#}-{period} convention from
                                          Stage B (debt_service.accrue_interest) and links to
                                          the AP Invoices page with a search prefilter. */}
                                      <td style={{ padding: '4px 8px' }} onClick={e => e.stopPropagation()}>
                                        {s.je_id ? (
                                          <a href={`/gl-workbench?je_id=${s.je_id}`}
                                             style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none', marginRight: 8 }}>
                                            JE #{s.je_id}
                                          </a>
                                        ) : <span style={{ color: 'var(--text-hint)' }}>—</span>}
                                        {s.status === 'POSTED' && (
                                          <a href={`/ap-invoices?search=DEBT-${expandedData.facility_number}-${s.period}`}
                                             style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none', fontSize: 10 }}
                                             title="View the AP open item created by Stage B's AP bridge">
                                            AP
                                          </a>
                                        )}
                                      </td>
                                      <td style={{ padding: '4px 8px' }} onClick={e => e.stopPropagation()}>
                                        {s.status === 'SCHEDULED' && expandedData.lifecycle_status === 'ACTIVE' && (
                                          <ActionButton label="Post" size="sm" variant="success"
                                            onClick={() => handleAccrue(expandedData.id, s.period)} />
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Drawdowns */}
                          {expandedData.drawdowns && expandedData.drawdowns.length > 0 && (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
                                DRAWDOWNS & REPAYMENTS ({expandedData.drawdowns.length})
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                <thead>
                                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                    {['Date', 'Type', 'Amount', 'Reference', 'JE'].map(h => (
                                      <th key={h} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 10, fontWeight: 700 }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {expandedData.drawdowns.map((d: any) => (
                                    <tr key={d.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                      <td style={{ padding: '4px 8px' }}>{d.transaction_date}</td>
                                      <td style={{ padding: '4px 8px' }}>
                                        <StatusBadge status={d.transaction_type} size="sm" />
                                      </td>
                                      <td style={{ padding: '4px 8px', fontWeight: 600,
                                        color: d.transaction_type === 'REPAYMENT' ? 'var(--positive)' : 'var(--text-primary)' }}>
                                        {d.transaction_type === 'REPAYMENT' ? '-' : '+'}{formatAmount(d.amount)}
                                      </td>
                                      <td style={{ padding: '4px 8px' }}>{d.reference || '-'}</td>
                                      <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{d.je_id || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 12, alignItems: 'center' }}>
              <ActionButton label="PREV" size="sm" variant="secondary" disabled={page === 0}
                onClick={() => setPage(Math.max(0, page - 1))} />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              </span>
              <ActionButton label="NEXT" size="sm" variant="secondary" disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage(page + 1)} />
            </div>
          )}
        </>
      )}

      {/* ═══ TAB: Payment Schedule ═══ */}
      {tab === 'Payment Schedule' && (
        <div style={{ marginTop: 12 }}>
          {schedule.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-hint)', padding: 32 }}>No scheduled payments.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Period', 'Total Payment', 'Interest', 'Principal', 'Facilities'].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schedule.map(s => (
                  <tr key={s.period}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{s.period}</td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{formatAmount(s.total_payment)}</td>
                    <td style={tdStyle}>{formatAmount(s.total_interest)}</td>
                    <td style={tdStyle}>{formatAmount(s.total_principal)}</td>
                    <td style={tdStyle}>{s.facility_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ═══ TAB: Maturity Profile ═══ */}
      {tab === 'Maturity Profile' && (
        <div style={{ marginTop: 12 }}>
          {maturity.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-hint)', padding: 32 }}>No active facilities with maturity dates.</div>
          ) : (
            <>
              {/* Simple bar visualization */}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
                {maturity.map(m => {
                  const maxAmt = Math.max(...maturity.map(x => x.amount));
                  const pct = maxAmt > 0 ? (m.amount / maxAmt) * 100 : 0;
                  return (
                    <div key={m.year} style={{ flex: '1 1 120px', maxWidth: 200, textAlign: 'center' }}>
                      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{formatAmount(m.amount)}</div>
                      <div style={{ height: 120, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                        <div style={{
                          width: 48, height: `${Math.max(8, pct)}%`, borderRadius: '6px 6px 0 0',
                          background: m.year <= new Date().getFullYear() ? '#ef4444' :
                            m.year <= new Date().getFullYear() + 1 ? '#f59e0b' : '#1e3a5f',
                        }} />
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>{m.year}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-hint)' }}>{m.count} facilit{m.count === 1 ? 'y' : 'ies'}</div>
                    </div>
                  );
                })}
              </div>

              {/* Detail table */}
              {maturity.map(m => (
                <div key={m.year} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{m.year} — {formatAmount(m.amount)}</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr>
                        {['Facility', 'Lender', 'Amount', 'Maturity Date'].map(h => (
                          <th key={h} style={{ ...thStyle, fontSize: 10 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {m.facilities.map((f: any) => (
                        <tr key={f.facility_number}>
                          <td style={tdStyle}>{f.facility_number}</td>
                          <td style={tdStyle}>{f.lender_name || '-'}</td>
                          <td style={{ ...tdStyle, fontWeight: 600 }}>{formatAmount(f.amount)}</td>
                          <td style={tdStyle}>{f.maturity_date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* ═══ Create Modal ═══ */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Debt Facility" size="lg"
        footer={<>
          <ActionButton label="Cancel" variant="secondary" onClick={() => setShowCreate(false)} />
          <ActionButton label="Create Facility" onClick={handleCreate} />
        </>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={lbl}>Facility Type</div>
              <select value={form.facility_type} onChange={e => setForm({ ...form, facility_type: e.target.value })}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 }}>
                {TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <div style={lbl}>Lender Name</div>
              <input value={form.lender_name} onChange={e => setForm({ ...form, lender_name: e.target.value })}
                placeholder="Bank / Institution" style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div>
              <div style={lbl}>Principal Amount *</div>
              <input type="number" value={form.original_principal} onChange={e => setForm({ ...form, original_principal: e.target.value })}
                placeholder="1000000" style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 }} />
            </div>
            <div>
              <div style={lbl}>Interest Rate * (decimal)</div>
              <input type="number" step="0.0001" value={form.interest_rate} onChange={e => setForm({ ...form, interest_rate: e.target.value })}
                placeholder="0.0525" style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 }} />
            </div>
            <div>
              <div style={lbl}>Rate Type</div>
              <select value={form.rate_type} onChange={e => setForm({ ...form, rate_type: e.target.value })}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 }}>
                <option value="FIXED">FIXED</option>
                <option value="VARIABLE">VARIABLE</option>
              </select>
            </div>
          </div>

          {form.rate_type === 'VARIABLE' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={lbl}>Benchmark</div>
                <input value={form.rate_benchmark} onChange={e => setForm({ ...form, rate_benchmark: e.target.value })}
                  placeholder="SOFR" style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 }} />
              </div>
              <div>
                <div style={lbl}>Spread (decimal)</div>
                <input type="number" step="0.0001" value={form.rate_spread} onChange={e => setForm({ ...form, rate_spread: e.target.value })}
                  placeholder="0.02" style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 }} />
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div>
              <div style={lbl}>Start Date *</div>
              <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 }} />
            </div>
            <div>
              <div style={lbl}>Maturity Date *</div>
              <input type="date" value={form.maturity_date} onChange={e => setForm({ ...form, maturity_date: e.target.value })}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 }} />
            </div>
            <div>
              <div style={lbl}>Payment Frequency</div>
              <select value={form.payment_frequency} onChange={e => setForm({ ...form, payment_frequency: e.target.value })}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 }}>
                {FREQUENCIES.map(f => <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={lbl}>Entity *</div>
              <select
                aria-label="Entity"
                value={form.entity_code}
                onChange={e => setForm({ ...form, entity_code: e.target.value })}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 }}
              >
                <option value="">Select entity...</option>
                {entities.map(en => (
                  <option key={en.entity_code} value={en.entity_code}>
                    {en.entity_code} — {en.entity_name} ({en.functional_currency})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div style={lbl}>Currency</div>
              <input value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={lbl}>Security Type</div>
              <select value={form.security_type} onChange={e => setForm({ ...form, security_type: e.target.value })}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 }}>
                <option value="">Select...</option>
                <option value="UNSECURED">Unsecured</option>
                <option value="SECURED">Secured</option>
                <option value="GUARANTEED">Guaranteed</option>
              </select>
            </div>
            <div>
              <div style={lbl}>Collateral</div>
              <input value={form.collateral_description} onChange={e => setForm({ ...form, collateral_description: e.target.value })}
                placeholder="Description of collateral" style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 }} />
            </div>
          </div>

          {/* GL Resolution Preview */}
          {glPreview && (
            <div style={{ padding: 12, borderRadius: 8, background: 'var(--accent-subtle)', border: '1px solid var(--border-subtle)', marginTop: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                GL Accounts (auto-resolved from your CoA — {glPreview.gl_resolution_method || 'N/A'}, {glPreview.gl_resolution_confidence || 0}% confidence)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {[
                  { l: 'Long-Term Debt', code: glPreview.debt_account },
                  { l: 'Current Portion', code: glPreview.current_portion_account },
                  { l: 'Interest Expense', code: glPreview.interest_expense_account },
                  { l: 'Interest Payable', code: glPreview.interest_payable_account },
                  { l: 'Cash / Bank', code: glPreview.cash_account },
                ].map(d => (
                  <div key={d.l} style={{ fontSize: 11 }}>
                    <span style={{ color: 'var(--text-hint)' }}>{d.l}: </span>
                    {d.code ? (
                      <>
                        <span style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>{d.code}</span>
                        {glPreview.account_names?.[d.code] && (
                          <span style={{ color: 'var(--text-secondary)' }}> — {glPreview.account_names[d.code]}</span>
                        )}
                      </>
                    ) : (
                      <span style={{ color: 'var(--negative)' }}>NOT RESOLVED — upload CoA or add manually</span>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 6, fontStyle: 'italic' }}>
                Note: loan agreement upload via Contract Intelligence is the recommended entry point.
                You can edit GL accounts on the facility detail after creation.
              </div>
            </div>
          )}
        </div>
      </Modal>
    </PageLayout>
  );
}
